import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Trash2, MessageSquare, Heart, Zap, Play, Trophy, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/time";

const KIND_ICON = {
  reply: MessageSquare,
  reaction: Heart,
  xp: Zap,
  level_up: Trophy,
  new_video: Play,
  unlock_progress: Lock,
  unlocked: Play,
  mention: MessageSquare,
};
const KIND_COLOR = {
  reply: "text-[#FF9F0A]",
  reaction: "text-[#E50914]",
  xp: "text-[#FF9F0A]",
  level_up: "text-[#FFD700]",
  new_video: "text-[#E50914]",
  unlock_progress: "text-[#FF9F0A]",
  unlocked: "text-[#32D74B]",
  mention: "text-[#E50914]",
};

export default function NotificationBell() {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [user, load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      await load();
      if (unread > 0) {
        try { await api.post("/notifications/read"); setUnread(0); } catch {}
      }
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/notifications");
      setItems([]); setUnread(0);
    } catch {}
  };

  const deleteOne = async (nid, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.delete(`/notifications/${nid}`);
      setItems((prev) => prev.filter((n) => n.id !== nid));
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropRef}>
      <button
        onClick={toggle}
        data-testid="notif-bell"
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 hover:text-white transition-colors"
      >
        <Bell className="w-4.5 h-4.5" />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#E50914] border-2 border-black text-white text-[10px] font-black flex items-center justify-center px-1 shadow-[0_0_10px_rgba(229,9,20,0.55)]"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] glass-strong rounded-xl overflow-hidden shadow-2xl border border-white/10 z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div>
              <div className="font-display font-black text-sm">Notifications</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{items.length} recent</div>
            </div>
            {items.length > 0 && (
              <button
                onClick={clearAll}
                data-testid="notif-clear-all"
                className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-[#E50914] inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-zinc-500 text-sm">
                No notifications yet.
              </div>
            )}
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              const iconColor = KIND_COLOR[n.kind] || "text-zinc-400";
              const content = (
                <div className="flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors group">
                  <div className={`w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 ${iconColor}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white line-clamp-2">{n.title}</div>
                    {n.body && <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-zinc-500 mt-1">{n.ts ? timeAgo(n.ts) : ""}</div>
                  </div>
                  <button
                    onClick={(e) => deleteOne(n.id, e)}
                    data-testid={`notif-del-${n.id}`}
                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-[#E50914] transition-opacity"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
              return n.link ? (
                <Link
                  key={n.id}
                  to={n.link}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => setOpen(false)}
                  className="block"
                >
                  {content}
                </Link>
              ) : (
                <div key={n.id} data-testid={`notif-item-${n.id}`}>{content}</div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
